import { useContext, useEffect } from 'react';
import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DataLoadingError from "@components/common/DataLoadingError";
import useSessionDetailsQuery from "@components/common/hooks/api/useSessionDetailsQuery";
import { API_URLS, axiosRequest } from "@lib/axios";
import { PageContext } from "@lib/contexts/pageContext";
import { AbilityContext, Subjects } from "@lib/permissions";
import ParticipantForm from "@components/Pages/Events/Sessions/Participants/ParticipantForm";
import { getParticipantName } from "@components/Pages/Events/Sessions/sessionFunctions";

const AddParticipant = () => {
  const ability = useAbility(AbilityContext);
  const {
    setBreadcrumbValue,
    setPageErrorMessage,
  } = useContext(PageContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { eventID, sessionID } = useParams();
  const { data: session, ...sessionQuery } = useSessionDetailsQuery(sessionID);

  useEffect(() => {
    if (session) {
      setBreadcrumbValue(':event', session.event);
      setBreadcrumbValue(':session', session);
    }
  }, [session, setBreadcrumbValue]);

  const addParticipantMutation = useMutation({
    mutationFn: async (data) => {
      const newParticipant = await axiosRequest({
        method: 'POST',
        url: `${API_URLS.PARTICIPANTS}`,
        data: {
          ...data,
          event_id: session.event_id,
          demographics_collected: false,  // this is only collected on the staff mobile app
        }
      });
      if (newParticipant) {
        await axiosRequest({
          method: 'PUT',
          url: `${API_URLS.PARTICIPANTS}/${newParticipant.id}/attendance`,
          data: {
            session_id: sessionID,
            status: 'PRESENT'
          }
        });
      }
      return newParticipant;
    },
    onSuccess: async (participant) => {
      setPageErrorMessage('');
      await queryClient.invalidateQueries({ queryKey: ['participants', eventID] });
      navigate(`/events/${eventID}/${sessionID}/participants`, {
        state: { message: `${getParticipantName(participant)} added to session.` }
      });
    },
    onError: async (err) => {
      setPageErrorMessage(['Error saving participant', err]);
      console.log('[FinalizeParticipants][save] error:', err);
    },
  });

  const onSubmit = async (data) => {
    addParticipantMutation.mutate(data);
  }

  if (sessionQuery.isLoading) {
    return (
      <Grid xs={12} textAlign="center">
        <CircularProgress/>
      </Grid>
    );
  }

  if (sessionQuery.isError && sessionQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load session!'} errors={sessionQuery.error.errors}/>;
  }

  if (!session) {
    return (
      <>
        You do not have access, or the session was not found.
      </>
    );
  }

  if (ability.cannot('edit', subject(Subjects.SESSION, session))) {
    return <>You do not have permission to edit this session.</>;
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        <ParticipantForm
          onSubmit={onSubmit}
        />
      </Grid>
    </Grid>
  );
};

export default AddParticipant;
